/**
 * Keeping a working person signed in.
 *
 * The session carries an idle timeout, and `loadSession` slides it on every
 * authenticated request. That is the right rule with a wrong assumption behind
 * it: it treats HTTP traffic as evidence the person is present, and in this
 * application the two came apart badly.
 *
 *   - Typing an article makes no requests at all. The editor holds the body in
 *     React state until somebody presses 保存する.
 *   - Reading makes none either, because the loader cache answers from memory.
 *   - The event stream connects once and stays open for hours; it authenticates
 *     at connect and never touches the session again.
 *
 * So an author writing for an hour issued zero requests, the idle window never
 * moved, and the stream's own session check - which runs every 60 seconds -
 * spotted the expiry and signed them out mid-sentence, losing whatever was in
 * the textarea. Measured on this database: session after session created with an
 * idle window of exactly 60 minutes, meaning not one request ever slid it.
 *
 * The fix is to make the heartbeat mean what the timeout is actually for. An
 * idle timeout protects an unattended machine, so it should follow the PERSON,
 * not the socket: while somebody is genuinely interacting, tell the server; when
 * they walk away, stop, and let the session expire exactly as intended.
 */
import { get } from './api.ts';

/**
 * How often a beat may be sent. Well inside the shortest idle window this
 * application ships (60 minutes), and low enough traffic - at most twelve
 * requests an hour, for an active user - to be irrelevant next to the loaders.
 */
const BEAT_MS = 5 * 60 * 1000;

/**
 * What counts as the person being here.
 *
 * Deliberately not `mousemove` or `scroll`: a trackpad nudge from a sleeping cat
 * would hold a session open forever, which is the failure an idle timeout exists
 * to prevent. These are all deliberate acts.
 */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'focus'] as const;

export function startKeepalive(): () => void {
  let active = false;
  let stopped = false;

  const noteActivity = () => {
    active = true;
  };

  for (const event of ACTIVITY_EVENTS) {
    // Passive and capturing: never blocks the interaction it is observing, and
    // still sees events that a handler below stops propagating.
    window.addEventListener(event, noteActivity, { passive: true, capture: true });
  }

  const timer = setInterval(() => {
    if (stopped) return;
    /*
     * Nothing happened, or the tab is in the background. Either way the person
     * is not working here, so the session is left to expire on schedule - the
     * whole point of the timeout.
     */
    if (!active || document.visibilityState !== 'visible') return;
    active = false;

    /*
     * Any authenticated request slides the window; `/auth/me` is the cheapest
     * one that already exists, so this adds no server surface. The answer is
     * discarded - the session state the app renders comes from the root loader.
     *
     * A failure is ignored on purpose. If the session is genuinely gone, the
     * next real request gets the 401 and the router's own handling takes over;
     * a beat is not the place to sign anybody out.
     */
    void get('/auth/me').catch(() => undefined);
  }, BEAT_MS);

  return () => {
    stopped = true;
    clearInterval(timer);
    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, noteActivity, { capture: true });
    }
  };
}
